import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Square, Clock, User, Car, Wrench, ArrowLeft, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const TimeTrackingPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const { woId } = useParams();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState(null);
  const [client, setClient] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [activeEntries, setActiveEntries] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [elapsed, setElapsed] = useState({});

  useEffect(() => { loadData(); }, [woId]);

  // Elapsed time ticker
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const newElapsed = {};
      activeEntries.forEach(e => {
        const start = new Date(e.start_time);
        const diff = Math.floor((now - start) / 1000);
        const h = Math.floor(diff / 3600);
        const m = Math.floor((diff % 3600) / 60);
        const s = diff % 60;
        newElapsed[e.id] = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      });
      setElapsed(newElapsed);
    }, 1000);
    return () => clearInterval(interval);
  }, [activeEntries]);

  const loadData = async () => {
    try {
      const [woRes, empRes] = await Promise.all([
        axios.get(`${API}/work-orders/${woId}`),
        axios.get(`${API}/employees`),
      ]);
      const wo = woRes.data;
      setWorkOrder(wo);
      setEmployees(empRes.data.filter(e => e.is_active));

      const [clientRes, vehicleRes, apptRes] = await Promise.all([
        axios.get(`${API}/clients/${wo.client_id}`).catch(() => null),
        axios.get(`${API}/vehicles/${wo.vehicle_id}`).catch(() => null),
        wo.appointment_id ? axios.get(`${API}/appointments/${wo.appointment_id}`).catch(() => null) : null,
      ]);
      if (clientRes) setClient(clientRes.data);
      if (vehicleRes) setVehicle(vehicleRes.data);
      if (apptRes) setAppointment(apptRes.data);

      await loadTimeEntries();
    } catch (err) {
      toast.error('Fiche introuvable');
      navigate('/admin/workshop');
    } finally { setLoading(false); }
  };

  const loadTimeEntries = async () => {
    const [activeRes, histRes] = await Promise.all([
      axios.get(`${API}/work-time/active`),
      axios.get(`${API}/work-time/history`, { params: { work_order_id: woId } }),
    ]);
    setActiveEntries(activeRes.data.filter(e => e.work_order_id === woId));
    setHistory(histRes.data.filter(e => e.status === 'completed'));
  };

  const startWork = async () => {
    if (!selectedEmployee) {
      toast.error('Sélectionnez un mécanicien');
      return;
    }
    try {
      await axios.post(`${API}/work-time/start`, { work_order_id: woId, employee_id: selectedEmployee });
      toast.success('Temps démarré');
      loadTimeEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const stopWork = async (employeeId) => {
    try {
      const res = await axios.post(`${API}/work-time/stop`, { work_order_id: woId, employee_id: employeeId });
      toast.success(`Temps arrêté (${res.data.duration_minutes} min)`);
      loadTimeEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const totalMinutes = history.reduce((sum, e) => sum + (e.duration_minutes || 0), 0);
  const totalH = Math.floor(totalMinutes / 60);
  const totalM = totalMinutes % 60;

  if (loading) return <AdminLayout auth={auth} onLogout={onLogout}><div className="p-8 text-center text-slate-500">{t('common.loading')}</div></AdminLayout>;
  if (!workOrder) return null;

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-to-workshop">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900" data-testid="timetracking-title">Pointeuse</h1>
            <p className="text-sm text-slate-500">Ref: {woId.substring(0, 8).toUpperCase()}</p>
          </div>
        </div>

        {/* Work Order Info */}
        <Card className="p-4 rounded-xl">
          <div className="flex flex-wrap gap-4 text-sm">
            {client && (
              <div className="flex items-center gap-2"><User className="w-4 h-4 text-slate-400" /><span>{client.first_name} {client.last_name}</span></div>
            )}
            {vehicle && (
              <div className="flex items-center gap-2"><Car className="w-4 h-4 text-slate-400" /><span>{vehicle.brand} {vehicle.model} - {vehicle.plate}</span></div>
            )}
            {appointment && (
              <div className="flex items-center gap-2"><Wrench className="w-4 h-4 text-slate-400" /><span>{appointment.category?.toUpperCase()}{appointment.intervention_type ? ` - ${appointment.intervention_type}` : ''}</span></div>
            )}
          </div>
        </Card>

        {/* Start Timer */}
        <Card className="p-4 rounded-xl space-y-4" data-testid="start-timer-section">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Play className="w-4 h-4" /> Demarrer le suivi</h3>
          <div className="flex gap-3 items-end">
            <div className="flex-1 space-y-2">
              <Label className="text-xs">Mecanicien</Label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger data-testid="select-mechanic">
                  <SelectValue placeholder="Sélectionnez..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={startWork}
              className="bg-emerald-600 hover:bg-emerald-700 h-10"
              disabled={!selectedEmployee}
              data-testid="start-timer-btn"
            >
              <Play className="mr-1 w-4 h-4" /> Demarrer
            </Button>
          </div>
        </Card>

        {/* Active Timers */}
        {activeEntries.length > 0 && (
          <Card className="p-4 rounded-xl space-y-3 border-emerald-200 bg-emerald-50" data-testid="active-timers">
            <h3 className="text-sm font-bold text-emerald-900 flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" /> En cours
            </h3>
            <div className="space-y-2">
              {activeEntries.map(entry => (
                <div key={entry.id} className="flex items-center justify-between bg-white p-3 rounded-lg" data-testid={`active-entry-${entry.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">
                      {entry.employee_name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{entry.employee_name}</p>
                      <p className="text-xs text-slate-500">Depuis {entry.start_time.substring(11, 16)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-mono font-bold text-emerald-700" data-testid={`timer-${entry.id}`}>
                      {elapsed[entry.id] || '00:00:00'}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700 border-red-200"
                      onClick={() => stopWork(entry.employee_id)}
                      data-testid={`stop-timer-${entry.id}`}
                    >
                      <Square className="mr-1 w-3 h-3" /> Stop
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* History */}
        <Card className="p-4 rounded-xl space-y-3" data-testid="time-history">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Clock className="w-4 h-4" /> Historique</h3>
            <span className="text-sm font-bold text-slate-900">Total: {totalH}h{totalM > 0 ? String(totalM).padStart(2, '0') : '00'}</span>
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Aucun temps enregistre</p>
          ) : (
            <div className="space-y-2">
              {history.map(entry => {
                const h = Math.floor(entry.duration_minutes / 60);
                const m = entry.duration_minutes % 60;
                return (
                  <div key={entry.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-xs font-bold">
                        {entry.employee_name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <p className="font-medium">{entry.employee_name}</p>
                        <p className="text-xs text-slate-500">{entry.start_time.substring(0, 10)} {entry.start_time.substring(11, 16)} - {entry.end_time?.substring(11, 16)}</p>
                      </div>
                    </div>
                    <span className="font-mono font-bold text-slate-700">{h}h{String(m).padStart(2, '0')}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
};

export default TimeTrackingPage;
